import type { IconName } from "./icons.js";
import type { UserRole } from "./storage.js";

// ---------- Tab Access Matrix (RBAC) ----------

/**
 * Maps each tab to the set of global roles that may access it.
 * If a tab is not listed, only superadmin can access it (fail-closed).
 */
const TAB_ACCESS: Record<string, readonly UserRole[]> = {
  chat: ["superadmin", "admin", "project_admin", "user"],
  overview: ["superadmin", "admin"],
  channels: ["superadmin", "admin"],
  sessions: ["superadmin", "admin"],
  usage: ["superadmin", "admin"],
  users: ["superadmin", "admin"],
  agents: ["superadmin", "admin"],
  projects: ["superadmin", "admin", "project_admin", "user"],
  tasks: ["superadmin", "admin", "project_admin", "user"],
  "agent-plans": ["superadmin", "admin", "project_admin", "user"],
  documents: ["superadmin"],
  canvas: ["superadmin"],
  cron: ["superadmin", "admin", "project_admin", "user"],
  "mcp-skills": ["superadmin", "admin"],
  n8n: ["superadmin"],
  pipelines: ["superadmin"],
  email: ["superadmin", "admin"],
  company: ["superadmin", "admin"],
  models: ["superadmin"],
  "skill-config": ["superadmin"],
  nodes: ["superadmin"],
  avatar: ["superadmin"],
  computer: ["superadmin"],
  "user-settings": ["superadmin"],
  "inference-config": ["superadmin"],
  security: ["superadmin"],
  debug: ["superadmin"],
  logs: ["superadmin"],
  "system-overview": ["superadmin"],
};

/** Check if a given global role can access a tab. */
export function canAccessTab(
  tab: Tab,
  role: UserRole | null | undefined,
  overrides?: Record<string, Record<string, boolean>>,
): boolean {
  if (!role) {
    return false;
  }
  if (role === "superadmin") {
    return true;
  }
  if (overrides && overrides[role] && tab in overrides[role]) {
    return overrides[role][tab];
  }
  const allowed = TAB_ACCESS[tab];
  if (!allowed) {
    return false;
  }
  return (allowed as readonly string[]).includes(role);
}

export const TAB_GROUPS = [
  { label: "", tabs: ["company"] },
  { label: "", tabs: ["chat"] },
  { label: "Control", tabs: ["overview", "channels", "sessions", "usage"] },
  {
    label: "i-GlooBot",
    tabs: [
      "users",
      "agents",
      "projects",
      "tasks",
      "agent-plans",
      "documents",
      "canvas",
      "cron",
      "mcp-skills",
      "n8n",
      "pipelines",
      "email",
    ],
  },
  {
    label: "Core Config",
    tabs: ["models", "skill-config", "nodes", "avatar", "computer", "user-settings", "inference-config"],
  },
  { label: "System", tabs: ["security", "debug", "logs", "system-overview"] },
] as const;

export type Tab =
  | "agents"
  | "overview"
  | "channels"
  | "sessions"
  | "usage"
  | "cron"
  | "projects"
  | "tasks"
  | "agent-plans"
  | "users"
  | "documents"
  | "canvas"
  | "mcp-skills"
  | "avatar"
  | "n8n"
  | "pipelines"
  | "computer"
  | "user-settings"
  | "inference-config"
  | "company"
  | "email"
  | "skill-config"
  | "skills"
  | "models"
  | "nodes"
  | "chat"
  | "security"
  | "settings"
  | "config"
  | "debug"
  | "logs"
  | "system-overview";

const TAB_PATHS: Record<Tab, string> = {
  agents: "/agents",
  overview: "/overview",
  channels: "/channels",
  sessions: "/sessions",
  usage: "/usage",
  cron: "/cron",
  projects: "/projects",
  tasks: "/tasks",
  "agent-plans": "/agent-plans",
  documents: "/documents",
  canvas: "/canvas",
  users: "/users",
  "mcp-skills": "/mcp-skills",
  avatar: "/avatar",
  n8n: "/n8n",
  pipelines: "/pipelines",
  computer: "/computer",
  "user-settings": "/user-settings",
  "inference-config": "/inference-config",
  company: "/company",
  email: "/email",
  "skill-config": "/skill-config",
  skills: "/skills",
  models: "/models",
  nodes: "/nodes",
  chat: "/chat",
  security: "/security",
  settings: "/settings",
  config: "/config",
  debug: "/debug",
  logs: "/logs",
  "system-overview": "/system-overview",
};

const PATH_TO_TAB = new Map(Object.entries(TAB_PATHS).map(([tab, path]) => [path, tab as Tab]));

export function normalizeBasePath(basePath: string): string {
  if (!basePath) {
    return "";
  }
  let base = basePath.trim();
  if (!base.startsWith("/")) {
    base = `/${base}`;
  }
  if (base === "/") {
    return "";
  }
  if (base.endsWith("/")) {
    base = base.slice(0, -1);
  }
  return base;
}

export function normalizePath(path: string): string {
  if (!path) {
    return "/";
  }
  let normalized = path.trim();
  if (!normalized.startsWith("/")) {
    normalized = `/${normalized}`;
  }
  if (normalized.length > 1 && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}

export function pathForTab(tab: Tab, basePath = ""): string {
  const base = normalizeBasePath(basePath);
  const path = TAB_PATHS[tab];
  return base ? `${base}${path}` : path;
}

export function tabFromPath(pathname: string, basePath = ""): Tab | null {
  const base = normalizeBasePath(basePath);
  let path = pathname || "/";
  if (base) {
    if (path === base) {
      path = "/";
    } else if (path.startsWith(`${base}/`)) {
      path = path.slice(base.length);
    }
  }
  let normalized = normalizePath(path).toLowerCase();
  if (normalized.endsWith("/index.html")) {
    normalized = "/";
  }
  if (normalized === "/") {
    return "chat";
  }
  return PATH_TO_TAB.get(normalized) ?? null;
}

export function inferBasePathFromPathname(pathname: string): string {
  let normalized = normalizePath(pathname);
  if (normalized.endsWith("/index.html")) {
    normalized = normalizePath(normalized.slice(0, -"/index.html".length));
  }
  if (normalized === "/") {
    return "";
  }
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "";
  }
  for (let i = 0; i < segments.length; i++) {
    const candidate = `/${segments.slice(i).join("/")}`.toLowerCase();
    if (PATH_TO_TAB.has(candidate)) {
      const prefix = segments.slice(0, i);
      return prefix.length ? `/${prefix.join("/")}` : "";
    }
  }
  return `/${segments.join("/")}`;
}

export function iconForTab(tab: Tab): IconName {
  switch (tab) {
    case "agents":
      return "folder";
    case "chat":
      return "messageSquare";
    case "overview":
      return "barChart";
    case "channels":
      return "link";
    case "sessions":
      return "fileText";
    case "usage":
      return "barChart";
    case "cron":
      return "loader";
    case "projects":
      return "folder";
    case "tasks":
      return "loader";
    case "agent-plans":
      return "check";
    case "documents":
      return "fileText";
    case "canvas":
      return "monitor";
    case "users":
      return "settings";
    case "mcp-skills":
      return "zap";
    case "avatar":
      return "settings";
    case "n8n":
      return "plug";
    case "pipelines":
      return "loader";
    case "computer":
      return "monitor";
    case "user-settings":
      return "settings";
    case "inference-config":
      return "barChart";
    case "company":
      return "globe";
    case "email":
      return "mail";
    case "skills":
      return "zap";
    case "models":
      return "settings";
    case "nodes":
      return "monitor";
    case "security":
      return "shield";
    case "settings":
      return "wrench";
    case "config":
      return "settings";
    case "debug":
      return "bug";
    case "logs":
      return "scrollText";
    case "system-overview":
      return "wrench";
    default:
      return "folder";
  }
}

export function titleForTab(tab: Tab) {
  switch (tab) {
    case "agents":
      return "Agents";
    case "overview":
      return "Overview";
    case "channels":
      return "Channels";
    case "sessions":
      return "Sessions";
    case "usage":
      return "Usage";
    case "cron":
      return "Cron Jobs";
    case "projects":
      return "Projects";
    case "tasks":
      return "Tasks";
    case "agent-plans":
      return "Agent Plans";
    case "documents":
      return "Documents";
    case "canvas":
      return "Canvas";
    case "users":
      return "Users";
    case "mcp-skills":
      return "Skills Server";
    case "avatar":
      return "Avatar";
    case "n8n":
      return "N8N Workflows";
    case "pipelines":
      return "Pipelines";
    case "computer":
      return "Computer";
    case "user-settings":
      return "User Settings";
    case "inference-config":
      return "Inference Config";
    case "company":
      return "Company";
    case "email":
      return "Email";
    case "skills":
      return "Skills";
    case "models":
      return "Models";
    case "nodes":
      return "Nodes";
    case "chat":
      return "Chat";
    case "security":
      return "Security";
    case "settings":
      return "Settings";
    case "config":
      return "Config";
    case "debug":
      return "Diagnostics";
    case "logs":
      return "Logs";
    case "system-overview":
      return "System Overview";
    default:
      return "Control";
  }
}

export function subtitleForTab(tab: Tab) {
  switch (tab) {
    case "agents":
      return "Manage agent workspaces, tools, and identities.";
    case "overview":
      return "Gateway status, entry points, and a fast health read.";
    case "channels":
      return "Channel status, per-agent credentials, and settings.";
    case "sessions":
      return "Inspect active sessions and adjust per-session defaults.";
    case "usage":
      return "";
    case "cron":
      return "Schedule wakeups and recurring agent runs.";
    case "projects":
      return "Manage multi-project configurations, system prompts, and user assignments.";
    case "tasks":
      return "Monitor outbound task orchestration, responses, and escalations.";
    case "agent-plans":
      return "Agent plan checklists projected from update_plan calls across sessions.";
    case "documents":
      return "Upload, search, read, and manage documents in the knowledge base.";
    case "canvas":
      return "Live preview of the Canvas Host content and declarative UI renderer.";
    case "users":
      return "Manage users, channel links, and project access.";
    case "mcp-skills":
      return "Create, edit, and manage skills on the i-GlooBot Skills Server.";
    case "avatar":
      return "Configure LiveKit + SadTalker avatar video calls and manage sessions.";
    case "n8n":
      return "Manage n8n workflow registrations, triggers, and webhook endpoints.";
    case "pipelines":
      return "Create, run, and monitor multi-step skill pipelines that chain skills together.";
    case "computer":
      return "OS-level desktop control: virtual displays, VNC, screenshots, and input automation.";
    case "user-settings":
      return "Toggle sidebar tab visibility per role.";
    case "inference-config":
      return "Set cost per million tokens for each model.";
    case "company":
      return "Manage company details, contact information, and business identity.";
    case "email":
      return "Configure SMTP and IMAP settings for the email channel.";
    case "skills":
      return "Browse, activate, configure, and manage agent skills.";
    case "models":
      return "Manage LLM providers, embedding models, API keys, and model configuration.";
    case "nodes":
      return "Paired devices, capabilities, and command exposure.";
    case "chat":
      return "";
    case "security":
      return "Manage security settings, allowlists, and hardening features.";
    case "settings":
      return "Agents, channels, messages, skills, and tools.";
    case "config":
      return "Gateway, authentication, environment, and system config.";
    case "debug":
      return "Raw gateway diagnostics, event log, and manual RPC for development.";
    case "logs":
      return "Live tail of the gateway file logs.";
    case "system-overview":
      return "Live tools, plugins, and config schema from the running gateway.";
    default:
      return "";
  }
}
